import React from 'react';

/**
 * Shared download progress overlay component
 * @param {Object} downloadState - Download state object with isDownloading, stage, current, total
 */
const DownloadProgressOverlay = ({ downloadState }) => {
  if (!downloadState?.isDownloading) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-surface rounded-2xl shadow-2xl px-8 py-8 flex flex-col items-center max-w-sm mx-4 border border-accent-30">
        <div className="relative mb-6">
          <svg className="animate-spin h-12 w-12 text-primary" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
          </svg>
          <div className="absolute inset-0 rounded-full border-2 border-primary-20 animate-ping"></div>
        </div>
        <h3 className="text-secondary font-semibold text-xl mb-2 text-center min-h-[28px]">
          {downloadState.stage === 'preparing' && 'Preparing download...'}
          {downloadState.stage === 'downloading' && (downloadState.total > 1 ? 'Downloading photos...' : 'Downloading file...')}
          {downloadState.stage === 'packaging' && 'Creating archive...'}
          {downloadState.stage === 'complete' && 'Download complete!'}
        </h3>
        <p className="text-secondary-70 text-sm text-center leading-relaxed mb-4 min-h-[48px] px-2">
          {downloadState.stage === 'preparing' && 'Getting download links ready...'}
          {downloadState.stage === 'downloading' && downloadState.total > 1 && `Downloading ${downloadState.current} of ${downloadState.total} files...`}
          {downloadState.stage === 'downloading' && downloadState.total === 1 && 'Downloading your file...'}
          {downloadState.stage === 'packaging' && 'Packaging your photos into a ZIP file...'}
          {downloadState.stage === 'complete' && 'Your download should start shortly!'}
        </p>
        <div className="w-full min-h-[44px] flex flex-col justify-center">
          {(downloadState.stage === 'downloading' || downloadState.stage === 'packaging') && downloadState.total > 0 && (
            <>
              <div className="w-full bg-primary-10 rounded-full h-3 overflow-hidden mb-2">
                <div 
                  className="bg-gradient-to-r from-primary to-primary-80 h-full rounded-full transition-all duration-300" 
                  style={{ width: `${Math.min(100, (downloadState.current / downloadState.total) * 100)}%` }}
                ></div>
              </div>
              {downloadState.total > 1 && (
                <div className="text-xs text-secondary-50 text-center">
                  {downloadState.current} / {downloadState.total} files
                </div>
              )}
            </>
          )}
        </div>
        {downloadState.stage === 'preparing' && (
          <div className="mt-6 w-full bg-primary-10 rounded-full h-3 overflow-hidden">
            <div className="bg-gradient-to-r from-primary to-primary-80 h-full rounded-full animate-pulse" style={{ width: '30%' }}></div>
          </div>
        )}
        {downloadState.stage === 'packaging' && downloadState.total === 0 && (
          <div className="mt-6 w-full bg-primary-10 rounded-full h-3 overflow-hidden">
            <div className="bg-gradient-to-r from-primary to-primary-80 h-full rounded-full animate-pulse" style={{ width: '90%' }}></div>
          </div>
        )}
        <div className="mt-4 text-xs text-secondary-50 text-center">
          Please don't close this page
        </div>
      </div>
    </div>
  );
};

export default DownloadProgressOverlay;
